import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LogoMark } from './ui/Logo';
import {
  AUTH_LOADING_SHELL_CLASS,
  AUTH_LOADING_SHELL_STYLE,
  AuthLoadingIndicator,
} from './auth/AuthLoadingIndicator';
import { capture } from '../lib/analytics';
import { claimClerkAutoRetry, clerkWakeRetrySpent } from '../lib/clerkLoadRetry';
import { useClerkWakeRetry } from '../hooks/useClerkWakeRetry';
import { claimAuthLoadFailureReport } from '../lib/authLoadFailureReport';
import { elapsedMsSince, monotonicNow } from '../lib/elapsedMs';
import { ClerkFailed } from '../lib/auth';
import { resolveEscapeDestination } from '../lib/clerkEscapeLink';

/**
 * How long to wait for Clerk to hydrate before assuming clerk-js
 * (CDN-hotloaded) never loaded. Exported so tests can pin the deadline.
 *
 * This is the BACKSTOP, not the primary detector, since <ClerkFailed> landed
 * below: Clerk reports `status === 'error'` as soon as it knows, and this timer
 * only has to cover the case where clerk-js never boots far enough to report
 * anything. The value remains a guess; `auth_load_succeeded.waited_ms` exists
 * to replace it with evidence.
 */
export const CLERK_LOAD_TIMEOUT_MS = 10_000;

/**
 * Failure copy, split so each half can be asserted and so AuthCard cannot drift.
 *
 * Written against NN/g's error-message guidelines
 * (https://www.nngroup.com/articles/error-message-guidelines):
 * - "Take a positive tone and don't blame the user" -> we own the failure.
 * - "Concisely and precisely describe the issue" -> name the blocked service.
 * - It deliberately no longer says "check your network or an ad blocker". That
 *   handed the user a diagnosis checklist for a cause our own custom Clerk
 *   domain (clerk.overlayqa.com, CNAME to frontend-api.clerk.services) already
 *   largely mitigates, and Clerk documents four causes, not two.
 */
export const CLERK_LOAD_FAILURE_HEADLINE = "We couldn't load sign-in";
export const CLERK_LOAD_FAILURE_DETAIL =
  "Something on this network is blocking our sign-in service. Nothing you've saved is affected.";

/**
 * The retry control's label, shared with AuthCard so the two surfaces cannot
 * drift. "Try again" over "Retry": plainer, and it reads as an invitation
 * rather than an instruction, per NN/g's positive-tone guideline.
 */
export const CLERK_LOAD_RETRY_LABEL = 'Try again';

/** Single string for surfaces that render one inline paragraph (AuthCard). */
export const CLERK_LOAD_FAILURE_MESSAGE =
  `${CLERK_LOAD_FAILURE_HEADLINE}. ${CLERK_LOAD_FAILURE_DETAIL}`;

/** Why we gave up. Rides auth_load_failed so the two paths stay separable. */
export type ClerkFailureReason = 'clerk_error' | 'timeout';

/**
 * Fires a callback when Clerk reports a hard failure.
 *
 * <ClerkFailed> is a render-prop-shaped control component (it returns children
 * only when status is 'error'), so this adapter turns "was rendered" into a
 * state transition the card can act on.
 */
function ClerkErrorSignal({ onError }: { onError: () => void }) {
  useEffect(() => {
    onError();
  }, [onError]);
  return null;
}

/**
 * Full-screen holding state ProtectedRoute renders while Clerk loads.
 * Pre-hydration, Clerk's <SignedIn>/<SignedOut> both render null, so without
 * this the page is blank during load and blank forever when clerk-js is
 * blocked (network failure / ad blocker / corporate proxy).
 *
 * Failure is detected two ways, deliberately: <ClerkFailed> (Clerk's own
 * status, fast and authoritative) and the CLERK_LOAD_TIMEOUT_MS backstop for a
 * script that never boots. Either way we reload the page ONCE automatically,
 * then fall back to this card. The automatic attempt exists because the manual
 * one was measurably not working: over 2026-08-05..08-23, 27 of 31 sessions
 * that reached the card ended there and only 3 of 28 people pressed the button,
 * while a reload provably recovers the load. See lib/clerkLoadRetry.ts for why
 * the claim is once-per-tab and fails closed.
 *
 * Because so few people press the button, the card also offers a way OUT that
 * does not need Clerk at all (lib/clerkEscapeLink.ts). Incident 2026-08-27: a
 * customer arrived from a public audit report, hit this card, and left in 51
 * seconds with nothing to click but Retry.
 */
export function AuthLoadingScreen() {
  const [timedOut, setTimedOut] = useState(false);
  // Set when the automatic reload has been claimed and fired. Keeps the spinner
  // up through the reload: rendering the failure card here would flash a dead
  // end at a user we are already rescuing.
  const [autoRetrying, setAutoRetrying] = useState(false);
  const [clerkErrored, setClerkErrored] = useState(false);
  // MONOTONIC start of the wait. Drives waited_ms on both outcomes so the
  // threshold above can eventually be tuned on real load times -- which is
  // exactly why it may not read the wall clock: Date.now() moves under an NTP
  // correction or a sleep/wake, and production carried timeout events claiming
  // a ~1.9s wait against this 10s deadline. See lib/elapsedMs.
  const startedAtRef = useRef(monotonicNow());
  // Deliberately NOT shared with any success latch. React.StrictMode (on in dev,
  // main.tsx:71) double-invokes effects mount -> unmount -> mount on the same
  // instance, and useRef survives that. A ref shared with an unmount-cleanup
  // reporter got latched by the synthetic unmount and silently disabled the
  // auto-retry for the whole dev session -- measured as reload called 0 times.
  const failureReportedRef = useRef(false);

  const handleClerkError = useCallback(() => setClerkErrored(true), []);

  useEffect(() => {
    const timer = window.setTimeout(() => setTimedOut(true), CLERK_LOAD_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, []);

  // Clerk's own verdict wins the label when both fire: it is the specific
  // cause, where the timer only ever means "nothing happened in time".
  const failureReason: ClerkFailureReason | null = clerkErrored
    ? 'clerk_error'
    : timedOut
      ? 'timeout'
      : null;

  // A Clerk outage here was invisible in product analytics (incident
  // 2026-07-29: a rescan-email conversion died on clerk.overlayqa.com timing
  // out, seen only as raw $exception noise). PostHog loads independently of
  // clerk-js, so capture works while Clerk is down.
  useEffect(() => {
    if (!failureReason || failureReportedRef.current) return;
    failureReportedRef.current = true;

    // Claim BEFORE reporting so `attempt` describes what actually happened:
    // attempt 1 is the failure we are about to auto-retry, attempt 2 is the
    // one the user is left looking at, and attempt 3 is that same card after
    // the wake retry below was also spent. claimClerkAutoRetry resolves and
    // guards sessionStorage itself; do not dereference it here.
    const willAutoRetry = claimClerkAutoRetry();

    // Report once per DOCUMENT, not once per instance. Two AuthLoadingScreens
    // can be live at the same time, and each used to emit its own copy of one
    // page-level outage -- measured live 2026-08-30 as same-second pairs with
    // different waited_ms and, on 08-29, disagreeing reasons. The claim is
    // deliberately NOT wrapped around the retry/suppression logic below: a
    // second instance must still call claimClerkAutoRetry and still set
    // autoRetrying, or it renders the dead end this branch exists to hide.
    // posthog-js flushes on pagehide, so this survives the reload below.
    if (claimAuthLoadFailureReport()) {
      capture('auth_load_failed', {
        surface: 'protected_route',
        reason: failureReason,
        attempt: willAutoRetry ? 1 : clerkWakeRetrySpent() ? 3 : 2,
        auto_retried: willAutoRetry,
        waited_ms: elapsedMsSince(startedAtRef.current),
      });
    }

    if (willAutoRetry) {
      setAutoRetrying(true);
      window.location.reload();
    }
  }, [failureReason]);

  // The matching `auth_load_succeeded` is reported by ProtectedRoute, not here.
  // This screen cannot see Clerk hydrate -- it only gets unmounted -- and an
  // unmount cleanup cannot tell "Clerk loaded" from "user navigated away" or
  // from StrictMode's synthetic remount. ProtectedRoute owns the real
  // isLoaded transition, so it is the honest place to measure the duration.

  // document.referrer is read once at mount: after the auto-retry reload it is
  // still the original inbound page, which is exactly the one we want.
  const escape = useMemo(
    () => resolveEscapeDestination(typeof document === 'undefined' ? '' : document.referrer),
    [],
  );

  const showFailure = failureReason !== null && !autoRetrying;

  // The automatic reload above retries at once, in the conditions that just
  // failed. While the card is showing, retry once more when the user comes
  // back to the tab or the network returns. Incident 2026-10-06: a trial owner
  // sat behind this card on two of his last three visits and never pressed
  // the button. Bounded in lib/clerkLoadRetry.ts.
  useClerkWakeRetry(showFailure, 'protected_route');

  return (
    <main className={AUTH_LOADING_SHELL_CLASS} style={AUTH_LOADING_SHELL_STYLE}>
      {/* Always mounted so Clerk can report an error before the timer fires. */}
      <ClerkFailed>
        <ClerkErrorSignal onError={handleClerkError} />
      </ClerkFailed>

      {showFailure ? (
        <section className="card-sm w-full max-w-sm text-center">
          <div className="flex justify-center">
            <LogoMark className="w-8 h-8" />
          </div>
          <h1 className="mt-4 text-base font-semibold text-gray-900">
            {CLERK_LOAD_FAILURE_HEADLINE}
          </h1>
          <p className="mt-2 text-sm leading-6 text-gray-600">{CLERK_LOAD_FAILURE_DETAIL}</p>
          <button
            type="button"
            className="btn-primary mt-5"
            onClick={() => {
              // posthog-js flushes on pagehide, so the event survives the reload.
              capture('auth_load_retry_clicked', { surface: 'protected_route' });
              window.location.reload();
            }}
          >
            <span className="text-cap-trim">{CLERK_LOAD_RETRY_LABEL}</span>
          </button>

          {/* NN/g: "Good errors ... protect the user's effort and time. Offer
              accelerators." Both of these work with clerk-js still blocked. */}
          <div className="mt-5 border-t border-gray-100 pt-4 text-sm leading-6 text-gray-600">
            <p>
              <a
                className="font-medium text-primary-600 underline hover:text-primary-700"
                href={escape.href}
                onClick={() =>
                  capture('auth_load_escape_clicked', {
                    surface: 'protected_route',
                    destination: escape.fromReferrer ? 'referrer' : 'fallback',
                  })
                }
              >
                {escape.fromReferrer
                  ? 'Go back to the page you came from'
                  : 'Go to the OverlayQA home page'}
              </a>
            </p>
            <p className="mt-1">
              <a
                className="font-medium text-primary-600 underline hover:text-primary-700"
                href="mailto:hello@overlayqa.com"
                onClick={() =>
                  capture('auth_load_escape_clicked', {
                    surface: 'protected_route',
                    destination: 'support_email',
                  })
                }
              >
                Email hello@overlayqa.com
              </a>
            </p>
          </div>
        </section>
      ) : (
        <AuthLoadingIndicator />
      )}
    </main>
  );
}
